import { PrismicRichText } from '@prismicio/react';
import InfoIconBase from '@remote-com/norma/icons/info.svg';
import { useRef } from 'react';
import { Button } from 'remote-com-shared/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import HeaderContext from 'remote-com-shared/components/Header/context';
import { staticAssetUrl } from 'remote-com-shared/domains/assets/helpers';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import { WebinarFormWrapped } from '../FormComponent';
import { Header, WebinarNavigation } from '../Header';
import CalendarIcon from '../assets/calendar-customer-events.svg';
import BgIllustration from '../assets/hero-svg-bg-left.svg';
import BottomBgIllustration from '../assets/hero-svg-bottom-right.svg';
import TopBgIllustration from '../assets/hero-svg-top-right.svg';

import { transformProperties } from '@/src/domains/prismic/helpers';
import { defaultPrismicComponents, Hyperlink } from '@/src/domains/prismic/render';
import {
  desktopBreakpoint,
  tabletBreakpoint,
  mobileBreakpoint,
  fromDesktopBreakpoint,
} from '@/src/domains/shared/breakpoints';

const Hero = styled.section`
  position: relative;
  color: var(--colors-blank);
  background-color: var(--colors-darkBlue);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-bottom: 100px;
`;

const HeroContentContainer = styled(ContentContainer)`
  z-index: 1;
  display: flex;

  ${fluidProp(['padding-top', 'padding-bottom'], '80px', '130px')}

  ${desktopBreakpoint(css`
    flex-direction: column;
    align-items: center;
  `)};

  ${mobileBreakpoint(css`
    padding-left: 0;
    padding-right: 0;
  `)};

  ${({ $direction }) => {
    switch ($direction) {
      case true:
        return css`
          flex-direction: column;
        `;
      case false:
      default:
        return css`
          flex-direction: row;
        `;
    }
  }}
`;

const ApplyFormWrapper = styled.div`
  ${fromDesktopBreakpoint(css`
    margin-left: 72px;
  `)}
`;

const HeroTitle = styled.div`
  font-family: var(--typography-fontBrand);
  ${({ theme }) => theme.typography.scale3}
  font-size: 62px;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

const IllustrationWrapper = styled.div`
  position: absolute;
  left: 0;
  top: 0;

  ${mobileBreakpoint(css`
    display: none;
  `)};
`;

const IllustrationWrapperTopRight = styled.div`
  position: absolute;
  right: 0;
  top: 0;

  ${tabletBreakpoint(css`
    display: none;
  `)};
`;

const IllustrationWrapperBottomRight = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;

  ${tabletBreakpoint(css`
    display: none;
  `)};
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const HeroContent = styled.div`
  max-width: 600px;
  padding-top: 20px;
  display: flex;
  flex-direction: column;

  ${desktopBreakpoint(css`
    margin-bottom: 50px;
  `)};

  ${mobileBreakpoint(css`
    padding-left: 30px;
    padding-right: 30px;
  `)};
`;

const DateWrapper = styled.div`
  margin-top: 17px;
`;

const CalendarWrapper = styled.div`
  display: flex;
  gap: 16px;
  color: #b7b8eb;
  font-family: var(--typography-fontBrand);
  ${({ theme }) => theme.typography.bodyLead}

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

const Day = styled.div`
  /* padding: 5px 5px 0 10px; */
`;

const Time = styled.div`
  display: flex;
  gap: 8px;
`;

const TimeZone = styled.div`
  :not(:last-child) {
    padding-right: 8px;
    border-right: 2px solid var(--colors-spindle);
  }
`;

const CTAButton = styled(Button)`
  && {
    background-color: var(--colors-blank);
    color: var(--colors-darkBlue);
    text-transform: uppercase;
    font-family: var(--typography-fontBrand);
    ${({ theme }) => theme.typography.subHead}
  }
`;

const VideoWrapper = styled.div`
  margin-top: 55px;

  ${tabletBreakpoint(css`
    margin-top: 0;
  `)};

  iframe {
    ${tabletBreakpoint(css`
      ${fluidProp('width', '320px', '850px')}
    `)};

    ${mobileBreakpoint(css`
      width: auto;
      height: auto;
    `)};
  }
`;

const HeroDescription = styled.div`
  ${({ theme }) => theme.typography.bodyLead}
  font-family: var(--typography-fontBrand);
  margin-top: 42px;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }

  ul,
  ol {
    margin-top: 46px;
    list-style-type: none;
  }

  li {
    margin-top: 20px;
    position: relative;

    &::after {
      content: '';
      background-color: white;
      -webkit-mask-image: url(${staticAssetUrl('/images/check-purple.svg')});
      mask-image: url(${staticAssetUrl('/images/check-purple.svg')});
      top: 10px;
      left: -30px;
      width: 15px;
      height: 10px;
      position: absolute;
      z-index: 2;
    }
  }
`;

const AdditionalInfo = styled.div`
  ${({ theme }) => theme.typography.bodyLead}
  font-family: var(--typography-fontBrand);
  display: flex;
  gap: 20px;
  margin-top: 80px;

  p {
    margin: 0;
  }

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;
const InfoIcon = styled(InfoIconBase)`
  height: 24px;
  width: 24px;
  flex-shrink: 0;
  margin-top: 0;
`;

const DateInfo = styled.div``;

const prismicComponents = {
  ...defaultPrismicComponents,
  hyperlink: ({
    node: {
      data: { url, target },
    },
    children,
  }) => <Hyperlink {...{ url, target, children }} $tone="inverted" />,
};

export function HeroSlice({ slice, context: { applyFormRef } }) {
  const { primary, context } = transformProperties(slice);
  const {
    pageTypeOnDemand,
    ctaTopRightButton,
    ctaTopRightButtonLabel,
    ctaTopRightButtonLink,
    heroTitle,
    heroDescription,
    infoText,
    heroFormTitle,
    heroFormDescription,
    heroFormButtonLabel,
    heroDate,
    heroUtc,
    heroGmt,
    heroEst,
    formEventName,
    vimeoVideoId,
  } = primary;
  const { hubspotFormDefinition } = context;
  const headerRef = useRef(null);

  return (
    <HeaderContext.Provider value={headerRef}>
      <Header>
        {ctaTopRightButton && (
          <WebinarNavigation>
            <CTAButton href={ctaTopRightButtonLink.url} target={ctaTopRightButtonLink?.target}>
              {ctaTopRightButtonLabel}
            </CTAButton>
          </WebinarNavigation>
        )}
      </Header>
      <Hero>
        <HeroContentContainer $direction={pageTypeOnDemand}>
          <HeroContent>
            {heroTitle && (
              <HeroTitle>
                <PrismicRichText field={heroTitle} />
              </HeroTitle>
            )}
            {heroDate && (
              <DateWrapper>
                <CalendarWrapper>
                  <CalendarIcon />
                  <DateInfo>
                    <Day>{heroDate}</Day>
                    <Time>
                      {heroUtc && <TimeZone>{heroUtc}</TimeZone>}
                      {heroGmt && <TimeZone>{heroGmt}</TimeZone>}
                      {heroEst && <TimeZone>{heroEst}</TimeZone>}
                    </Time>
                  </DateInfo>
                </CalendarWrapper>
              </DateWrapper>
            )}
            {heroDescription && (
              <HeroDescription>
                <PrismicRichText field={heroDescription} components={prismicComponents} />
              </HeroDescription>
            )}
            {infoText && (
              <AdditionalInfo>
                <InfoIcon />
                <PrismicRichText field={infoText} components={prismicComponents} />
              </AdditionalInfo>
            )}
          </HeroContent>
          {!pageTypeOnDemand ? (
            <ApplyFormWrapper ref={applyFormRef}>
              <WebinarFormWrapped
                hubspotFormDefinition={hubspotFormDefinition}
                modalFormTitle={heroFormTitle}
                modalFormDescription={heroFormDescription}
                modalFormSubmitLabel={heroFormButtonLabel}
                eventName={formEventName}
              />
            </ApplyFormWrapper>
          ) : (
            vimeoVideoId && (
              <VideoWrapper>
                <iframe
                  title="webinarVimeo"
                  src={`https://player.vimeo.com/video/${vimeoVideoId}`}
                  width="640"
                  height="360"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              </VideoWrapper>
            )
          )}
        </HeroContentContainer>
        <IllustrationWrapper>
          <ImageContainer>
            <BgIllustration />
          </ImageContainer>
        </IllustrationWrapper>
        <IllustrationWrapperTopRight>
          <ImageContainer>
            <TopBgIllustration />
          </ImageContainer>
        </IllustrationWrapperTopRight>
        <IllustrationWrapperBottomRight>
          <ImageContainer>
            <BottomBgIllustration />
          </ImageContainer>
        </IllustrationWrapperBottomRight>
      </Hero>
    </HeaderContext.Provider>
  );
}
